import { supabase } from "@/integrations/supabase/client";

export interface PublicAnuncio {
  id: string;
  item_id: string | null;
  item_nome: string;
  tipo: string;
  preco: number;
  moeda: string;
  preco_coins: number | null;
  preco_kk: number | null;
  servidor: string | null;
  destaque: boolean;
  status: string;
  duracao_dias: number;
  expira_em: string | null;
  created_at: string;
  updated_at: string;
  descricao: string | null;
  tier: number | null;
  apelido: string | null;
  lote_id: string | null;
  item_custom: boolean;
  anunciante_id: string | null;
  anunciante_avatar_url: string | null;
  preferencias_contato: string[] | null;
  renovacao_pendente: boolean | null;
  tem_whatsapp: boolean;
  tem_email: boolean;
  aprovado_em: string | null;
  valor_taxa: number;
  taxa_paga: boolean;
  metodo_pagamento_taxa: string | null;
  comprovante_coins_url: string | null;
  motivo_encerramento: string | null;
  abacatepay_id: string | null;
  abacatepay_url: string | null;
  destaque_inicio?: string | null;
  destaque_fim?: string | null;

  cliente_perfil_id?: string | null;
  revendedor?: boolean;
  personagem?: PersonagemData | null;
}

export interface PersonagemData {
  anuncio_id: string;
  personagem_nome: string;
  level: number | null;
  vocacao: string | null;
  mundo: string | null;
  sexo: string | null;
  guild: string | null;
  servidor: string | null;
  magic: number;
  club: number;
  fist: number;
  sword: number;
  fishing: number;
  axe: number;
  distance: number;
  shielding: number;
  magic_pct?: number;
  club_pct?: number;
  fist_pct?: number;
  sword_pct?: number;
  fishing_pct?: number;
  axe_pct?: number;
  distance_pct?: number;
  shielding_pct?: number;
  weekly_task: boolean;
  prey_slot: boolean;
  charm_expansion: boolean;
  loot_pouch?: boolean;
  auras?: string[];
  itens: string[];
  quests: string[];
  bosses: string[];
  charms: string[];
  boss_points?: number;
  charm_points?: number;
  loyalty_points?: number;
  outfits?: string[];
  montarias?: string[];
  battle_pass?: number[];
  midias?: { nome: string; path: string; tipo: string }[];
}

export interface PaginatedResult {
  anuncios: PublicAnuncio[];
  total: number;
  page?: number;
  pageSize?: number;
}

interface FetchPublicAnunciosParams {
  ids?: string[];
  advertiserId?: string;
  apelidos?: string[];
  itemNames?: string[];
  search?: string;
  tipo?: string;
  excludeTipos?: string[];
  servidores?: string[];
  tiers?: number[];
  minReais?: number;
  maxReais?: number;
  minCoins?: number;
  maxCoins?: number;
  minKk?: number;
  maxKk?: number;
  page?: number;
  pageSize?: number;
  destaqueOnly?: boolean;
  excludeDestaque?: boolean;
  propostasOnly?: boolean;
  includeExpired?: boolean;
  viewerProfileId?: string;
}

const cleanArray = (values?: string[]) =>
  (values || []).map((value) => value?.trim()).filter(Boolean) as string[];

export const fetchPublicAnuncios = async (
  params: FetchPublicAnunciosParams = {},
): Promise<PublicAnuncio[]> => {
  const result = await fetchPublicAnunciosPaginated(params);
  return result.anuncios;
};

export const fetchPublicAnunciosPaginated = async (
  params: FetchPublicAnunciosParams = {},
): Promise<PaginatedResult> => {
  const ids = cleanArray(params.ids);
  const itemNames = cleanArray(params.itemNames);

  if (params.ids && ids.length === 0) return { anuncios: [], total: 0 };
  if (params.itemNames && itemNames.length === 0) return { anuncios: [], total: 0 };

  const apelidos = cleanArray(params.apelidos);
  const { data, error } = await supabase.functions.invoke("public-anuncios", {
    body: {
      ids: ids.length > 0 ? ids : undefined,
      advertiserId: params.advertiserId?.trim() || undefined,
      apelidos: apelidos.length > 0 ? apelidos : undefined,
      itemNames: itemNames.length > 0 ? itemNames : undefined,
      search: params.search?.trim() || undefined,
      tipo: params.tipo?.trim() || undefined,
      excludeTipos: params.excludeTipos && params.excludeTipos.length > 0 ? params.excludeTipos : undefined,
      servidores: params.servidores && params.servidores.length > 0 ? params.servidores : undefined,
      tiers: params.tiers && params.tiers.length > 0 ? params.tiers : undefined,
      minReais: params.minReais,
      maxReais: params.maxReais,
      minCoins: params.minCoins,
      maxCoins: params.maxCoins,
      minKk: params.minKk,
      maxKk: params.maxKk,
      page: params.page,
      pageSize: params.pageSize,
      destaqueOnly: params.destaqueOnly,
      excludeDestaque: params.excludeDestaque,
      propostasOnly: params.propostasOnly,
      includeExpired: params.includeExpired,
      viewerProfileId: params.viewerProfileId?.trim() || undefined,
    },
  });

  if (error) throw error;
  if (data?.error) throw new Error(data.error);

  return {
    anuncios: Array.isArray(data?.anuncios) ? (data.anuncios as PublicAnuncio[]) : [],
    total: typeof data?.total === "number" ? data.total : 0,
    page: data?.page,
    pageSize: data?.pageSize,
  };
};

export const getPublicAnuncioById = async (id: string): Promise<PublicAnuncio | null> => {
  const anuncios = await fetchPublicAnuncios({ ids: [id] });
  return anuncios[0] || null;
};
